import { useEffect, useRef, useState } from "react";
import { yUndoPluginKey } from "y-prosemirror";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import { MessageSquarePlus, PenLine } from "lucide-react";
import { CommentHighlight, setComments, anchorFromSelection, flatten, findRange } from "@/components/rich/CommentHighlight";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCursor from "@tiptap/extension-collaboration-cursor";
import { useAuth } from "@/context/AuthContext";
import { useInvalidate } from "@/lib/queries";
import { DocSocket } from "@/lib/docSocket";
import { baseExtensions, getMarkdown } from "@/components/rich/extensions";
import { RichToolbar } from "@/components/rich/RichToolbar";
import { draft, merge3, mergeAttrs } from "@/lib/draftStore";
import { cacheLocal } from "@/lib/offline/engine";

const STATUS = { connecting: ["Connecting…", "#A8A090"], live: ["Live", "#10B981"], saving: ["Saving…", "#F59E0B"], saved: ["Saved", "#10B981"], offline: ["Offline · saved on device", "#F59E0B"], denied: ["No access", "#F87171"] };

const People = ({ awareness, selfId }) => {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    const upd = () => setPeople([...awareness.getStates().entries()].filter(([id, s]) => id !== selfId && s.user).map(([id, s]) => ({ id, ...s.user })));
    upd();
    awareness.on("change", upd);
    return () => awareness.off("change", upd);
  }, [awareness, selfId]);
  return (
    <div className="flex -space-x-1.5" data-testid="live-notes-people">
      {people.map((p) => (
        <span key={p.id} title={`${p.name} is editing`} className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#13171F] text-[10px] font-bold text-[#0B0D10]" style={{ background: p.color }} data-testid="live-notes-person">
          {p.name?.[0]?.toUpperCase() || "?"}
        </span>
      ))}
    </div>
  );
};

const mergeInto = (editor, sock, base, ours, theirs) => {
  const b = base || theirs;
  const md = merge3(b.md, ours.md, theirs.md);
  if (md !== getMarkdown(editor)) editor.commands.setContent(md);
  const summary = merge3(b.summary || "", ours.summary || "", theirs.summary || "");
  const attrs = mergeAttrs(b.attributes, ours.attributes, theirs.attributes);
  sock.doc.transact(() => {
    const t = sock.doc.getText("summary");
    if (t.toString() !== summary) { t.delete(0, t.length); t.insert(0, summary); }
    const m = sock.doc.getMap("attributes");
    [...m.keys()].forEach((k) => { if (!(k in attrs)) m.delete(k); });
    Object.entries(attrs).forEach(([k, v]) => { if (m.get(k) !== String(v)) m.set(k, String(v)); });
  });
};

const Session = ({ ent, canEdit, onReset, onMarkdown, comments, onLive }) => {
  const invalidate = useInvalidate();
  const [sock, setSock] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [init, setInit] = useState(null);
  const timer = useRef(null);
  const editorRef = useRef(null);
  useEffect(() => {
    const flush = () => {
      const md = getMarkdown(editorRef.current);
      onMarkdown(md);
      draft.set(ent.id, s.snapshot(md));
      if (s.isOpen()) { s.saveAll(md); cacheLocal(ent.id, { content: md, ...s.fields() }); setStatus("saving"); } else setStatus("offline");
    };
    const s = new DocSocket(ent.id, {
      onInit: setInit,
      onStatus: setStatus,
      onReset,
      onFieldsSaved: () => invalidate(["entity", ent.id]),
      onSaved: () => { if (!timer.current) { draft.clear(ent.id); setStatus("saved"); } },
      onOtherSaved: () => { if (editorRef.current) s.base = { md: getMarkdown(editorRef.current), ...s.fields() }; },
      onReconnect: (m) => {
        const ed = editorRef.current;
        if (!ed) return;
        if (!m.updates.length) mergeInto(ed, s, s.base, s.snapshot(getMarkdown(ed)), { md: m.content || "", summary: m.summary || "", attributes: m.attributes || {} });
        clearTimeout(timer.current);
        timer.current = null;
        flush();
      },
      onLocalChange: () => {
        setStatus(s.isOpen() ? "saving" : "offline");
        clearTimeout(timer.current);
        timer.current = setTimeout(() => { timer.current = null; flush(); }, 1200);
      },
    });
    setSock(s);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current); timer.current = null;
        const md = getMarkdown(editorRef.current);
        draft.set(ent.id, s.snapshot(md));
        if (s.isOpen() && !s.stale) s.saveAll(md);
      }
      onLive?.(null);
      s.destroy();
      invalidate(["entity", ent.id], ["history", ent.id]);
    };
  }, [ent.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sock && init) onLive?.({ sock, init, status }); }, [sock, init, status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!sock) return null;
  return <SessionEditor sock={sock} canEdit={canEdit} init={init} status={status} setStatus={setStatus} editorRef={editorRef} onMarkdown={onMarkdown} comments={comments} onLive={onLive} />;
};

const SessionEditor = ({ sock, canEdit, init, status, setStatus, editorRef, onMarkdown, comments }) => {
  const { user } = useAuth();
  const pickRef = useRef(null);
  pickRef.current = comments?.onPick;

  const editor = useEditor({
    extensions: [
      ...baseExtensions({ collab: true, placeholder: canEdit ? "Write the history, appearance, secrets… everyone here edits together." : "" }),
      Collaboration.configure({ document: sock.doc }),
      CollaborationCursor.configure({ provider: sock, user: { name: user?.name || "Author", color: "#F59E0B" } }),
      CommentHighlight.configure({ onPick: (id) => pickRef.current?.(id) }),
    ],
    editable: false,
    editorProps: { attributes: { class: "lf-prose focus:outline-none px-4 py-4 min-h-[180px]", "data-testid": "live-notes-content" } },
  }, [sock]);
  editorRef.current = editor;

  useEffect(() => {
    if (!editor || !init) return;
    editor.commands.updateUser({ name: init.you.name, color: init.you.color });
    if (init.seed) {
      sock.suppress = true;
      if (init.content) editor.commands.setContent(init.content, false);
      sock.seedFields(init.summary, init.attributes);
      sock.suppress = false;
      yUndoPluginKey.getState(editor.state)?.undoManager?.clear();
    }
    editor.setEditable(canEdit && !init.readonly);
    const d = draft.get(sock.eid);
    if (d) { draft.clear(sock.eid); if (canEdit && !init.readonly) mergeInto(editor, sock, d.base, d, sock.snapshot(getMarkdown(editor))); }
    onMarkdown(getMarkdown(editor));
    setStatus("live");
  }, [editor, init]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (init) setComments(editor, comments?.list || [], comments?.active); }, [editor, init, comments?.list, comments?.active]);
  useEffect(() => {
    if (!editor || !comments?.active || !comments.scroll) return;
    const el = editor.view.dom.querySelector(`[data-comment-id="${comments.active}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [editor, comments?.active, comments?.scroll]);
  useEffect(() => {
    if (!editor || !comments?.onFound) return undefined;
    const calc = () => { const f = flatten(editor.state.doc); comments.onFound(new Set((comments.list || []).filter((c) => findRange(f, c)).map((c) => c.id))); };
    calc();
    editor.on("update", calc);
    return () => editor.off("update", calc);
  }, [editor, init, comments?.list]); // eslint-disable-line react-hooks/exhaustive-deps
  const request = (kind = "comment") => { const a = anchorFromSelection(editor.state); if (a && (a.quote || kind === "suggestion")) comments.onRequest({ ...a, kind }); };
  useEffect(() => {
    if (!comments?.applyRef) return;
    comments.applyRef.current = (c) => {
      const r = findRange(flatten(editor.state.doc), c);
      if (!r) return false;
      editor.chain().insertContentAt({ from: r.from, to: r.to }, c.replacement ? { type: "text", text: c.replacement } : "").run();
      return true;
    };
  }, [editor, comments?.applyRef]);

  const [label, color] = STATUS[status];
  const editable = canEdit && init && !init.readonly;
  return (
    <div className="rounded-md border border-[#2D3442] bg-[#13171F] focus-within:border-[#D97706]/50" data-testid="live-notes">
      {editable ? (
        <RichToolbar editor={editor} testid="live-notes-toolbar" extra={<span className="flex shrink-0 items-center gap-2 pl-2">
          {comments && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => request()} disabled={editor?.state.selection.empty} title="Comment on selected text" aria-label="Comment on selected text" data-testid="live-notes-toolbar-comment"
            className="flex h-8 w-8 items-center justify-center rounded text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F59E0B] disabled:opacity-30"><MessageSquarePlus className="h-4 w-4" /></button>}
          {comments && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => request("suggestion")} title="Suggest an edit (select text to replace, or place the cursor to insert)" aria-label="Suggest an edit" data-testid="live-notes-toolbar-suggest"
            className="flex h-8 w-8 items-center justify-center rounded text-[#A8A090] hover:bg-[#1A202C] hover:text-[#10B981]"><PenLine className="h-4 w-4" /></button>}<People awareness={sock.awareness} selfId={sock.doc.clientID} /><span className="text-[11px]" style={{ color }} data-testid="live-notes-status">{label}</span></span>} />
      ) : (
        <div className="flex items-center justify-end gap-2 border-b border-[#2D3442] px-3 py-1.5"><People awareness={sock.awareness} selfId={sock.doc.clientID} /><span className="text-[11px]" style={{ color }} data-testid="live-notes-status">{label}</span></div>
      )}
      {!init && <div className="px-4 py-4 text-sm text-[#A8A090]">Opening the shared page…</div>}
      {editor && editable && comments && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 120, placement: "top" }} shouldShow={({ state }) => !state.selection.empty}>
          <span className="flex overflow-hidden rounded-md border border-[#D97706]/60 bg-[#13171F] shadow-lg">
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => request()} data-testid="comment-bubble-button"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#F59E0B] hover:bg-[#1A202C]"><MessageSquarePlus className="h-3.5 w-3.5" />Comment</button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => request("suggestion")} data-testid="suggest-bubble-button"
              className="flex items-center gap-1.5 border-l border-[#2D3442] px-2.5 py-1.5 text-xs font-medium text-[#6EE7B7] hover:bg-[#1A202C]"><PenLine className="h-3.5 w-3.5" />Suggest</button>
          </span>
        </BubbleMenu>
      )}
      <EditorContent editor={editor} />
    </div>
  );
};

export const LiveNotes = ({ ent, canEdit, onMarkdown, comments, onLive }) => {
  const [session, setSession] = useState(0);
  const [paused, setPaused] = useState(false);
  const reset = () => { onLive?.(null); setPaused(true); setTimeout(() => { setSession((s) => s + 1); setPaused(false); }, 700); };
  if (paused) return <div className="rounded-md border border-[#2D3442] bg-[#13171F] px-4 py-6 text-sm text-[#A8A090]" data-testid="live-notes-reloading">Refreshing the latest version…</div>;
  return <Session key={`${ent.id}-${session}`} ent={ent} canEdit={canEdit} onReset={reset} onMarkdown={onMarkdown} comments={comments} onLive={onLive} />;
};
